import React, { useCallback, useEffect, useRef, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Loader2, RefreshCw, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { ChartConfig } from "@/components/ui/chart";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  reportesAPI,
  type DashboardPendienteFactura,
  type DashboardPendienteRemitoCompra,
  type DashboardPendienteRemitoVtas,
  type DashboardSerieResponse,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { visibleDashboardCards } from "@/lib/dashboardCards";
import type { ModulePermissionEntry } from "@/lib/modulePermissions";
import {
  MODULE_PROVEEDORES,
  MODULE_REMITOS_VTAS,
  MODULE_VENTAS,
} from "@/lib/modulePermissions";

type StatsMode = "ventas" | "compras" | "merge";
type StatsRange = 1 | 3 | 6 | 12;

type LoadState<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: T };

interface DashboardOperativoProps {
  modulosPermitidos: number[];
  modulePermissions?: ModulePermissionEntry[] | null;
  userRol?: string;
  isElevated?: boolean;
  openModuleByRoute: (route: string) => void;
}

function hasModule(modulos: number[], id: number): boolean {
  return modulos.includes(id);
}

function moneyTick(v: number) {
  if (!Number.isFinite(v)) return "";
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}

function formatArs(n: number): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(n);
}

function parseMoney(s: string | undefined): number {
  const n = parseFloat(s || "0");
  return Number.isFinite(n) ? n : 0;
}

function estadoBadgeClass(estado: string): string {
  const e = estado.toLowerCase();
  if (e === "parcial") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
  }
  return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
}

function PendientesCard({
  title,
  count,
  loading,
  error,
  emptyLabel,
  onRetry,
  onVerTodos,
  canNavigate = true,
  children,
}: {
  title: string;
  count?: number;
  loading: boolean;
  error?: string | null;
  emptyLabel: string;
  onRetry: () => void;
  onVerTodos: () => void;
  canNavigate?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Card className="gap-4 py-4 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 px-4 pb-0">
        <CardTitle className="text-sm font-semibold tracking-wide text-foreground/90">
          {title}
        </CardTitle>
        {!loading && !error && count != null && (
          <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
      </CardHeader>
      <CardContent className="flex min-h-[180px] flex-col gap-3 px-4 pt-0">
        {loading && (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando…
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Reintentar
            </Button>
          </div>
        )}
        {!loading && !error && count === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm font-medium text-muted-foreground">{emptyLabel}</p>
          </div>
        )}
        {!loading && !error && count != null && count > 0 && children}
        {!loading && !error && canNavigate && (
          <div className="mt-auto pt-1">
            <Button variant="secondary" size="sm" className="w-full" onClick={onVerTodos}>
              Ver todos
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PendienteRow({
  canNavigate,
  onOpen,
  children,
}: {
  canNavigate: boolean;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  const className = "flex w-full items-center gap-2 py-2 text-left text-sm";
  if (canNavigate) {
    return (
      <button
        type="button"
        className={cn(className, "transition-colors hover:bg-muted/40")}
        onClick={onOpen}
      >
        {children}
      </button>
    );
  }
  return <div className={className}>{children}</div>;
}

export function DashboardOperativo({
  modulosPermitidos,
  modulePermissions,
  userRol,
  isElevated,
  openModuleByRoute,
}: DashboardOperativoProps) {
  const cards = visibleDashboardCards(modulePermissions, { userRol, isElevated });
  const canRemitosVtas = cards.remitosVtas;
  const canProveedores = cards.remitosCompras;
  const canVentas = cards.facturas;
  const showPendientes = canRemitosVtas || canProveedores || canVentas;
  const showStats = cards.stats;
  const canOpenRemitosVtas = hasModule(modulosPermitidos, MODULE_REMITOS_VTAS);
  const canOpenProveedores = hasModule(modulosPermitidos, MODULE_PROVEEDORES);
  const canOpenVentas = hasModule(modulosPermitidos, MODULE_VENTAS);

  const [pendientes, setPendientes] = useState<
    LoadState<{
      remitosVtas: { count: number; items: DashboardPendienteRemitoVtas[] };
      remitosCompras: { count: number; items: DashboardPendienteRemitoCompra[] };
      facturas: { count: number; items: DashboardPendienteFactura[] };
    }>
  >({ status: "loading" });

  const [mode, setMode] = useState<StatsMode>("ventas");
  const [range, setRange] = useState<StatsRange>(6);
  const [yoy, setYoy] = useState(false);
  const [serie, setSerie] = useState<LoadState<DashboardSerieResponse>>({ status: "loading" });
  const [statsRefreshing, setStatsRefreshing] = useState(false);

  const loadPendientes = useCallback(async () => {
    if (!showPendientes) return;
    setPendientes({ status: "loading" });
    try {
      const data = await reportesAPI.dashboardPendientes({ limit: 8 });
      setPendientes({
        status: "ok",
        data: {
          remitosVtas: data.remitosVtas,
          remitosCompras: data.remitosCompras,
          facturas: data.facturas,
        },
      });
    } catch (e: any) {
      setPendientes({
        status: "error",
        message: e?.message || "No se pudieron cargar los pendientes",
      });
    }
  }, [showPendientes]);

  const loadSerie = useCallback(async () => {
    if (!showStats) return;
    setSerie({ status: "loading" });
    try {
      const data = await reportesAPI.dashboardSerie({ meses: range, yoy });
      setSerie({ status: "ok", data });
    } catch (e: any) {
      setSerie({
        status: "error",
        message: e?.message || "No se pudo cargar la serie",
      });
    }
  }, [showStats, range, yoy]);

  useEffect(() => {
    void loadPendientes();
  }, [loadPendientes]);

  const refreshStats = useCallback(async () => {
    if (!showStats) return;
    setStatsRefreshing(true);
    try {
      await reportesAPI.refreshDashboardSerie();
      const data = await reportesAPI.dashboardSerie({ meses: range, yoy });
      setSerie({ status: "ok", data });
      if (showPendientes) {
        try {
          const pend = await reportesAPI.dashboardPendientes({ limit: 8 });
          setPendientes({
            status: "ok",
            data: {
              remitosVtas: pend.remitosVtas,
              remitosCompras: pend.remitosCompras,
              facturas: pend.facturas,
            },
          });
        } catch {
          // Pendientes es best-effort; el gráfico ya se actualizó.
        }
      }
    } catch {
      try {
        const data = await reportesAPI.dashboardSerie({ meses: range, yoy });
        setSerie({ status: "ok", data });
      } catch (e: any) {
        setSerie({
          status: "error",
          message: e?.message || "No se pudo cargar la serie",
        });
      }
    } finally {
      setStatsRefreshing(false);
    }
  }, [showStats, range, yoy, showPendientes]);

  const refreshStatsRef = useRef(refreshStats);
  refreshStatsRef.current = refreshStats;

  useEffect(() => {
    if (!showStats) return;
    void refreshStatsRef.current();
  }, [showStats, range, yoy]);

  const chartData =
    serie.status === "ok"
      ? serie.data.categories.map((label, i) => ({
          label,
          ventas: parseMoney(serie.data.ventas[i]),
          compras: parseMoney(serie.data.compras[i]),
          ventasYoy: parseMoney(serie.data.ventasYoy?.[i]),
          comprasYoy: parseMoney(serie.data.comprasYoy?.[i]),
        }))
      : [];

  const chartConfig = {
    ventas: { label: "Ventas", color: "var(--chart-1)" },
    compras: { label: "Compras", color: "var(--chart-2)" },
    ventasYoy: { label: "Ventas año ant.", color: "var(--chart-3)" },
    comprasYoy: { label: "Compras año ant.", color: "var(--chart-4)" },
  } satisfies ChartConfig;

  const sumVentas = chartData.reduce((a, r) => a + r.ventas, 0);
  const sumCompras = chartData.reduce((a, r) => a + r.compras, 0);
  const rangeLabel = range === 1 ? "1 mes" : `${range} meses`;

  const pendientesLoading = pendientes.status === "loading";
  const pendientesError = pendientes.status === "error" ? pendientes.message : null;
  const pData = pendientes.status === "ok" ? pendientes.data : null;

  return (
    <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-8 pb-16">
        {showPendientes && (
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground/80">
                Pendientes
              </h2>
              <div className="ml-2 h-px flex-1 bg-muted/50" />
            </div>
            <div
              className={cn(
                "grid gap-4",
                [canRemitosVtas, canProveedores, canVentas].filter(Boolean).length >= 3
                  ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                  : "grid-cols-1 md:grid-cols-2",
              )}
            >
              {canRemitosVtas && (
                <PendientesCard
                  title="Remitos Vtas"
                  count={pData?.remitosVtas.count}
                  loading={pendientesLoading}
                  error={pendientesError}
                  emptyLabel="Sin remitos pendientes"
                  onRetry={loadPendientes}
                  onVerTodos={() => openModuleByRoute("/remitos-vtas")}
                  canNavigate={canOpenRemitosVtas}
                >
                  <ul className="divide-y divide-border/60">
                    {pData?.remitosVtas.items.map((row) => (
                      <li key={row.id}>
                        <PendienteRow
                          canNavigate={canOpenRemitosVtas}
                          onOpen={() => openModuleByRoute("/remitos-vtas")}
                        >
                          <span className="font-medium tabular-nums">{row.numero}</span>
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {row.clienteNombre}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium capitalize",
                              estadoBadgeClass(row.estado),
                            )}
                          >
                            {row.estado}
                          </span>
                        </PendienteRow>
                      </li>
                    ))}
                  </ul>
                </PendientesCard>
              )}

              {canProveedores && (
                <PendientesCard
                  title="Remitos Compras"
                  count={pData?.remitosCompras.count}
                  loading={pendientesLoading}
                  error={pendientesError}
                  emptyLabel="Sin remitos pendientes"
                  onRetry={loadPendientes}
                  onVerTodos={() => openModuleByRoute("/proveedores")}
                  canNavigate={canOpenProveedores}
                >
                  <ul className="divide-y divide-border/60">
                    {pData?.remitosCompras.items.map((row) => (
                      <li key={row.id}>
                        <PendienteRow
                          canNavigate={canOpenProveedores}
                          onOpen={() => openModuleByRoute("/proveedores")}
                        >
                          <span className="font-medium tabular-nums">{row.numero}</span>
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {row.proveedorNombre}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium capitalize",
                              estadoBadgeClass(row.estado),
                            )}
                          >
                            {row.estado}
                          </span>
                        </PendienteRow>
                      </li>
                    ))}
                  </ul>
                </PendientesCard>
              )}

              {canVentas && (
                <PendientesCard
                  title="Facturas a finalizar"
                  count={pData?.facturas.count}
                  loading={pendientesLoading}
                  error={pendientesError}
                  emptyLabel="Sin facturas pendientes"
                  onRetry={loadPendientes}
                  onVerTodos={() => openModuleByRoute("/ventas")}
                  canNavigate={canOpenVentas}
                >
                  <ul className="divide-y divide-border/60">
                    {pData?.facturas.items.map((row) => (
                      <li key={row.id}>
                        <PendienteRow
                          canNavigate={canOpenVentas}
                          onOpen={() => openModuleByRoute("/ventas")}
                        >
                          <span className="font-medium tabular-nums">{row.numero}</span>
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {row.clienteNombre}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium capitalize",
                              estadoBadgeClass(row.estado),
                            )}
                          >
                            {row.estado}
                          </span>
                        </PendienteRow>
                      </li>
                    ))}
                  </ul>
                </PendientesCard>
              )}
            </div>
          </section>
        )}

        {showStats && (
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground/80">
                Estadísticas
              </h2>
              <div className="ml-2 h-px flex-1 bg-muted/50" />
            </div>

            <Card className="gap-4 py-4 shadow-sm">
              <CardContent className="space-y-4 px-4">
                <div className="flex flex-wrap items-center gap-3">
                  <ToggleGroup
                    type="single"
                    value={mode}
                    onValueChange={(v) => v && setMode(v as StatsMode)}
                    variant="outline"
                    size="sm"
                    className="h-8 justify-start"
                  >
                    <ToggleGroupItem value="ventas" className="px-2.5 text-xs">
                      Ventas
                    </ToggleGroupItem>
                    <ToggleGroupItem value="compras" className="px-2.5 text-xs">
                      Compras
                    </ToggleGroupItem>
                    <ToggleGroupItem value="merge" className="px-2.5 text-xs">
                      Merge
                    </ToggleGroupItem>
                  </ToggleGroup>

                  <div className="hidden h-4 w-px bg-border sm:block" />

                  <ToggleGroup
                    type="single"
                    value={String(range)}
                    onValueChange={(v) => v && setRange(Number(v) as StatsRange)}
                    variant="outline"
                    size="sm"
                    className="h-8 justify-start"
                  >
                    {([1, 3, 6, 12] as StatsRange[]).map((r) => (
                      <ToggleGroupItem key={r} value={String(r)} className="px-2.5 text-xs">
                        {r === 1 ? "1 mes" : `${r} m`}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>

                  <div className="flex items-center gap-2 sm:ml-auto">
                    <Switch id="dash-yoy" checked={yoy} onCheckedChange={setYoy} />
                    <Label htmlFor="dash-yoy" className="text-xs text-muted-foreground">
                      Año anterior (YoY)
                    </Label>
                    <TooltipProvider delayDuration={400}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            title="Actualizar estadísticas"
                            disabled={statsRefreshing}
                            onClick={() => void refreshStats()}
                            className="h-8 w-8 p-0 flex items-center justify-center group"
                          >
                            <RotateCw
                              className={cn(
                                "h-4 w-4 transition-transform duration-500",
                                statsRefreshing
                                  ? "animate-spin"
                                  : "group-hover:rotate-180",
                              )}
                            />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Actualizar estadísticas</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {serie.status === "ok" && (
                  <div
                    className={cn(
                      "grid gap-3",
                      mode === "merge" ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2",
                    )}
                  >
                    {(mode === "ventas" || mode === "merge") && (
                      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Ventas · {rangeLabel}
                        </p>
                        <p className="text-lg font-semibold tabular-nums text-foreground">
                          {formatArs(sumVentas)}
                        </p>
                      </div>
                    )}
                    {(mode === "compras" || mode === "merge") && (
                      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Compras · {rangeLabel}
                        </p>
                        <p className="text-lg font-semibold tabular-nums text-foreground">
                          {formatArs(sumCompras)}
                        </p>
                      </div>
                    )}
                    {mode === "merge" && (
                      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Delta venta − gasto
                        </p>
                        <p
                          className={cn(
                            "text-lg font-semibold tabular-nums",
                            sumVentas - sumCompras >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-destructive",
                          )}
                        >
                          {formatArs(sumVentas - sumCompras)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {serie.status === "loading" && (
                  <div className="flex h-[260px] items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando serie…
                  </div>
                )}

                {serie.status === "error" && (
                  <div className="flex h-[220px] flex-col items-center justify-center gap-3">
                    <p className="text-sm text-destructive">{serie.message}</p>
                    <Button variant="outline" size="sm" onClick={loadSerie}>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      Reintentar
                    </Button>
                  </div>
                )}

                {serie.status === "ok" && (
                  <ChartContainer
                    config={chartConfig}
                    className="aspect-auto h-[min(320px,55vh)] w-full min-w-0 justify-normal items-stretch"
                  >
                    <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 8, left: 8 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
                      <XAxis
                        dataKey="label"
                        tickLine={{ stroke: "var(--border)" }}
                        axisLine={{ stroke: "var(--border)" }}
                        tick={{ fill: "var(--foreground)", fontSize: 11 }}
                      />
                      <YAxis
                        tickLine={{ stroke: "var(--border)" }}
                        axisLine={{ stroke: "var(--border)" }}
                        tick={{ fill: "var(--foreground)", fontSize: 11 }}
                        tickFormatter={moneyTick}
                        width={48}
                        domain={[0, "auto"]}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) => (
                              <span className="font-mono tabular-nums">
                                {formatArs(Number(value))}
                              </span>
                            )}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      {(mode === "ventas" || mode === "merge") && (
                        <Line
                          type="monotone"
                          dataKey="ventas"
                          stroke="var(--color-ventas)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                      {(mode === "compras" || mode === "merge") && (
                        <Line
                          type="monotone"
                          dataKey="compras"
                          stroke="var(--color-compras)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                      {yoy && (mode === "ventas" || mode === "merge") && (
                        <Line
                          type="monotone"
                          dataKey="ventasYoy"
                          stroke="var(--color-ventasYoy)"
                          strokeWidth={1.5}
                          strokeDasharray="4 4"
                          dot={false}
                        />
                      )}
                      {yoy && (mode === "compras" || mode === "merge") && (
                        <Line
                          type="monotone"
                          dataKey="comprasYoy"
                          stroke="var(--color-comprasYoy)"
                          strokeWidth={1.5}
                          strokeDasharray="4 4"
                          dot={false}
                        />
                      )}
                    </LineChart>
                  </ChartContainer>
                )}

                <p className="text-[11px] text-muted-foreground">
                  Neto mensual · Ventas confirmadas (NC restan) · Compras = remitos finalizados
                </p>
              </CardContent>
            </Card>
          </section>
        )}

        {!showPendientes && !showStats && (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 bg-muted/10 px-6 text-center">
            <p className="text-sm font-medium text-foreground/80">Sin módulos operativos</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Tu rol no tiene cards del Inicio. En Roles → Dashboard / Inicio tildá cuáles
              mostrar, o usá el menú Inicio para abrir los módulos disponibles.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
